import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Box, Button, FormControl, FormHelperText, Link, Stack, Typography } from "@mui/joy";
import { ApiCreditSystemAdmin, ApiUpdateCreditSystemPayload } from "griptonite-api-client";
import { HTTPError } from "griptonite-api-client/client";
import { ReactNode, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import Warning from "@/assets/icons/Warning";
import { useUpdateCreditSystemMutation, useVenueCreditSystemQuery } from "@/hooks/credits";
import { useCurrentVenueQuery } from "@/hooks/venues";
import { handleFormError } from "@/utils/handleFormError";

import CheckboxInput from "../forms/CheckboxInput";
import { MediaSelectInput } from "../forms/MediaInput";
import TextInput, { NumberInput } from "../forms/TextInput";
import { getExposureWarning, shouldShowCanadaBalanceNote } from "./creditLimitWarnings";

export const SUPPORT_EMAIL = "support@griptonite.io";
const ONTARIO_REWARD_POINTS_URL = "https://www.ontario.ca/page/reward-points";

const capSchema = z.number().int().min(1, { message: "Must be at least 1" }).nullable().optional();

const CreditSystemFormSchema = z.object({
  name: z.string().trim().min(1, { message: "Name is required" }),
  // A logo that's still uploading has no uuid yet, so this also stops the form
  // saving until the upload finishes.
  logo: z
    .object(
      { uuid: z.string(), url: z.string(), type: z.string() },
      { message: "Wait for the logo to finish uploading" },
    )
    .nullable(),
  requireCheckinAccount: z.boolean(),
  maxOutstandingCredits: capSchema,
  maxAccountBalance: capSchema,
  creditLifetimeDays: z
    .number({ message: "Expiry is required" })
    .int()
    .min(1, { message: "Must be at least 1 day" })
    .max(3650, { message: "Must be at most 3650 days" }),
});

type CreditSystemFormValues = z.infer<typeof CreditSystemFormSchema>;

const creditSystemToFormValues = (system?: ApiCreditSystemAdmin): CreditSystemFormValues => ({
  name: system?.name ?? "",
  logo: system?.logo ?? null,
  requireCheckinAccount: system?.requireCheckinAccount ?? false,
  maxOutstandingCredits: system?.maxOutstandingCredits ?? null,
  maxAccountBalance: system?.maxAccountBalance ?? null,
  creditLifetimeDays: system?.creditLifetimeDays ?? 180,
});

const formValuesToPayload = (values: CreditSystemFormValues): ApiUpdateCreditSystemPayload => ({
  name: values.name,
  logo: values.logo?.uuid ?? null,
  requireCheckinAccount: values.requireCheckinAccount,
  maxOutstandingCredits: values.maxOutstandingCredits,
  maxAccountBalance: values.maxAccountBalance,
  creditLifetimeDays: values.creditLifetimeDays,
});

export default function ManageCreditSystem() {
  const { data: venue } = useCurrentVenueQuery();
  const isAdmin = venue?.userRole === "ADMIN";
  const query = useVenueCreditSystemQuery(isAdmin ? venue?.uuid : undefined);

  let content;
  if (venue && !isAdmin) {
    content = <Alert color="neutral">Only gym admins can manage credits.</Alert>;
  } else if (query.error instanceof HTTPError && query.error.response.status === 404) {
    content = (
      <Alert color="neutral">
        <Typography>
          Credits aren’t set up for this gym yet. To get started, contact{" "}
          <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>.
        </Typography>
      </Alert>
    );
  } else if (query.error) {
    content = <Alert color="danger">Couldn’t load the credit settings.</Alert>;
  } else if (query.data) {
    content = <CreditSystemForm system={query.data} />;
  }

  return (
    <Box sx={{ height: "100%", overflowY: "auto" }}>
      <Stack spacing={2} padding={2} sx={{ maxWidth: 640 }}>
        <Typography level="title-lg">Credits</Typography>
        {content}
      </Stack>
    </Box>
  );
}

const CreditSystemForm = ({ system }: { system: ApiCreditSystemAdmin }) => {
  const { data: venue } = useCurrentVenueQuery();
  const defaultValues = useMemo(() => creditSystemToFormValues(system), [system]);

  const methods = useForm({
    mode: "onBlur",
    resolver: zodResolver(CreditSystemFormSchema),
    defaultValues,
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const updateMutation = useUpdateCreditSystemMutation(venue?.uuid, system.slug);

  const onSubmit = async (values: CreditSystemFormValues) => {
    try {
      await updateMutation.mutateAsync(formValuesToPayload(values));
    } catch (error) {
      handleFormError(error, methods);
    }
  };

  // Account linking needs a checkin provider. Leave the box enabled while it's
  // ticked so it can still be switched off.
  const canRequireCheckinAccount =
    !!venue?.checkinProvider || methods.watch("requireCheckinAccount");
  const requireCheckinAccountError = methods.formState.errors.requireCheckinAccount?.message;
  const checkinProviderName = venue?.checkinProvider?.displayName;
  const requireCheckinAccountLabel = checkinProviderName
    ? `Require a linked ${checkinProviderName} account`
    : "Require a linked checkin account";
  let requireCheckinAccountHelpText;
  if (!canRequireCheckinAccount) {
    requireCheckinAccountHelpText =
      "Your gym's checkin system doesn't support account linking, so this can't be turned on.";
  } else if (checkinProviderName) {
    requireCheckinAccountHelpText = `Climbers must link their ${checkinProviderName} account to Griptonite before they can earn credits.`;
  } else {
    requireCheckinAccountHelpText =
      "Climbers must link their gym membership account before they can earn credits.";
  }

  const [maxAccountBalance, maxOutstandingCredits] = methods.watch([
    "maxAccountBalance",
    "maxOutstandingCredits",
  ]);
  const exposureWarning = getExposureWarning(maxOutstandingCredits, maxAccountBalance);
  const showCanadaBalanceNote = shouldShowCanadaBalanceNote(
    venue?.country,
    maxAccountBalance,
    system.creditValue,
  );

  return (
    <FormProvider {...methods}>
      <Stack
        spacing={3}
        component="form"
        autoComplete="off"
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <Box>
          <Typography level="body-sm" sx={{ color: "neutral.600" }}>
            These settings apply to every gym in {system.name}:
          </Typography>
          <Typography level="body-sm" fontWeight="bold">
            {system.venues.map((v) => v.name).join(", ")}
          </Typography>
        </Box>

        <Box>
          <TextInput name="name" label="Wallet name" />
          <Typography level="body-sm" sx={{ color: "neutral.600" }} marginTop={1}>
            Shown to climbers on their credits wallet.
          </Typography>
        </Box>

        <MediaSelectInput
          name="logo"
          label="Wallet logo"
          accept="image/*"
          multiple={false}
          helpText="Shown on the wallet, which also takes its colours from it."
        />

        <FormControl error={!!requireCheckinAccountError}>
          <CheckboxInput
            name="requireCheckinAccount"
            label={requireCheckinAccountLabel}
            disabled={!canRequireCheckinAccount}
          />
          <Typography level="body-sm" sx={{ color: "neutral.600" }} marginTop={1}>
            {requireCheckinAccountHelpText}
          </Typography>
          {requireCheckinAccountError ? (
            <FormHelperText>{requireCheckinAccountError}</FormHelperText>
          ) : null}
        </FormControl>

        <Box>
          <NumberInput name="creditLifetimeDays" label="Credit expiry" endDecorator="days" />
          <Typography level="body-sm" sx={{ color: "neutral.600" }} marginTop={1}>
            How long credits last after they’re earned. Credits expire at midnight. Changes only
            apply to credits earned from now on.
          </Typography>
        </Box>

        <Box>
          <NumberInput
            name="maxAccountBalance"
            label="Maximum balance per climber"
            placeholder="(unlimited)"
          />
          <Typography level="body-sm" sx={{ color: "neutral.600" }} marginTop={1}>
            The most credits one climber can hold. Awards that would take them over this are cut
            short.
          </Typography>
          {showCanadaBalanceNote ? <CanadaBalanceNote /> : null}
        </Box>

        <Box>
          <NumberInput
            name="maxOutstandingCredits"
            label="Maximum exposure"
            placeholder="(unlimited)"
          />
          <Typography level="body-sm" sx={{ color: "neutral.600" }} marginTop={1}>
            Limits the total amount of unspent credits across all climbers. You’ll be notified when
            the amount comes close to the limit.
          </Typography>
          {exposureWarning ? <LimitWarning>{exposureWarning}</LimitWarning> : null}
        </Box>

        <Box>
          <Button
            type="submit"
            loading={methods.formState.isSubmitting}
            disabled={!methods.formState.isDirty}
          >
            Save
          </Button>
        </Box>
      </Stack>
    </FormProvider>
  );
};

const LimitWarning = ({ children }: { children: ReactNode }) => (
  <Alert startDecorator={<Warning />} sx={{ marginTop: 1.5 }} color="warning">
    <Typography level="body-sm">{children}</Typography>
  </Alert>
);

const CanadaBalanceNote = () => (
  <LimitWarning>
    In some provinces, letting climbers hold more than $50 CAD of credits can affect whether those
    credits are allowed to expire.{" "}
    <Link href={ONTARIO_REWARD_POINTS_URL} target="_blank" rel="noopener noreferrer">
      Find out more
    </Link>
  </LimitWarning>
);
